import { Link, NavLink } from "react-router";
import { NAV } from "../content/landing";
import { useAuth, type AuthStatus } from "../features/auth/context";
import { modelsHref } from "../features/auth/destinations";
import { Container } from "./Container";
import { Cta } from "./Cta";
import { Wordmark } from "./Wordmark";
import styles from "./Nav.module.css";
import { isAppContext } from "../lib/appContext";

/**
 * The one nav control that depends on who's looking.
 *
 * Signed in it points at the account page, which is where Sign out already
 * lives — a second control in the bar would buy nothing and cost the 390px
 * layout, which fits four items only because none of them wraps.
 */
function AuthControl() {
  const { status } = useAuth();

  if (status === "loading") {
    /*
     * Boot window: reserve the box, show nothing in it.
     *
     * Rendering the Sign in CTA here would flash it at a user who IS signed in,
     * and the moment that window is guaranteed to be hit is the return from
     * Stripe — a paying customer's first impression is not the place to suggest
     * they're signed out. This is the same reason RequireAuth waits on
     * "loading" instead of bouncing to /login.
     *
     * The spacer is a real (hidden) CTA rather than a hand-measured width, so
     * the reserved box tracks Cta's geometry instead of drifting from it.
     * `visibility: hidden` keeps it out of the tab order and the a11y tree.
     */
    return (
      <span className={styles.authSlot} aria-hidden="true">
        <Cta variant="secondary" tabIndex={-1}>
          {NAV.signIn}
        </Cta>
      </span>
    );
  }

  if (status === "authenticated") {
    return (
      <Cta to="/app/account" variant="secondary">
        {NAV.account}
      </Cta>
    );
  }

  /* BOTH doors, as siblings (Ned, 2026-08-07). A visitor who has an account and
     one who does not need different things, and the bar cannot tell them apart —
     showing only Sign in made Create account reachable solely by noticing a
     small link at the bottom of the sign-in page.

     This is the one place the 390px constraint bites: the bar fits four items
     because none of them wraps, and this slot now holds two. They are the two
     shortest labels on the page for that reason, and Nav.module.css lets this
     pair wrap as a unit rather than letting either half break. */
  return (
    <span className={styles.authPair}>
      <Cta to="/login" variant="secondary">
        {NAV.signIn}
      </Cta>
      <Cta to="/signup">{NAV.createAccount}</Cta>
    </span>
  );
}

/**
 * The signed-in bar: Fixtures · Insights · Fantasy · Account.
 *
 * THE DECISION (Ned, 2026-09-07): fixtures are the landing spot, Premium
 * lives inside the fixture page, the Insights dashboard is a separate nav
 * with a tool menu, and Franco's fantasy rooms are their own area. That last
 * item was a disabled "Franco — Soon" span until Franco's fantasy UI merged
 * onto this branch the same day (routes/Fantasy.tsx, `/app/fantasy/:sport?`)
 * — it is a real `Link` now, and the label reads "Fantasy" rather than
 * "Franco": this bar names sections by what they are (Fixtures, Insights),
 * and "Franco" stays as the persona's name in the page's own body copy, not
 * as what a reader clicks to get there. Replaces Models / The Board /
 * Pricing for an authenticated reader: those three are the sales funnel, and
 * someone signed in is already past it.
 *
 * Same item COUNT as the signed-out bar (three text links + `AuthControl`),
 * which matters here more than it would elsewhere — Nav.module.css's `.links`
 * layout fits exactly four items in a 390px bar because none of them wraps,
 * and that arithmetic is unchanged only because this swaps content, not
 * cardinality.
 */
/**
 * "You are here" — the one thing this bar never said.
 *
 * A reader arrives having spent their time on other sites, and every one of
 * them marks the section you are in. Ours marked none, so the bar looked the
 * same on Fixtures as on Fantasy and the only way to know where you were was to
 * read the heading. `ModelRail` and the fixture list already do this (both set
 * `aria-current`); the PRIMARY nav was the one that didn't.
 *
 * `NavLink` sets `aria-current="page"` on the active item by itself — that is
 * the half a screen reader needs. `.linkActive` is the half everyone else sees.
 *
 * ⚠️ `end` ON `/app` IS NOT OPTIONAL. `/app` is a prefix of `/app/insights` and
 * `/app/fantasy`, so without it FIXTURES stays lit on both of the others and
 * the bar claims you are in three sections at once — confidently wrong, which
 * is worse than the silence it replaced.
 */
function navClass({ isActive }: { isActive: boolean }) {
  return isActive ? `${styles.link} ${styles.linkActive}` : styles.link;
}

function AppLinks() {
  return (
    <>
      <NavLink to="/app" end className={navClass}>
        {NAV.fixtures}
      </NavLink>
      <NavLink to="/app/insights" className={navClass}>
        {NAV.insights}
      </NavLink>
      <NavLink to="/app/fantasy" className={navClass}>
        {NAV.fantasy}
      </NavLink>
    </>
  );
}

/** The signed-out / boot-window bar: Models · The Board · Pricing — the
    funnel, unchanged by the 2026-09-07 landing-spot decision. */
function MarketingLinks({ status }: { status: AuthStatus }) {
  return (
    <>
      {/* Same label, destination by session: "Models" means the marketing
          section to a visitor and YOUR models to a subscriber. Reusing this
          item rather than adding a fifth is deliberate — the 390px bar fits
          four only because none of them wraps. Never reached while
          authenticated (see `Nav`'s branch below), so its authenticated
          branch only matters for the instant between sign-in resolving and
          the bar swapping to `AppLinks`. */}
      {/* The signed-out target carries a hash (`/why-kev#the-models`) and
          `NavLink` matches on PATHNAME, so this lights up anywhere on
          /why-kev rather than only when the reader has jumped to that
          section. That is the right answer for a bar with no "Why Kev"
          item of its own: the reader is on that page, and this is the item
          that took them there. */}
      <NavLink to={modelsHref(status)} className={navClass}>
        {NAV.models}
      </NavLink>
      <NavLink to="/board" className={navClass}>
        {NAV.board}
      </NavLink>
      <NavLink to="/pricing" className={navClass}>
        {NAV.pricing}
      </NavLink>
    </>
  );
}

export function Nav() {
  const { status } = useAuth();
  /* Opened from inside the iPhone app — see lib/appContext.ts. The whole
     marketing funnel comes off the bar: Pricing is the item Guideline 3.1.1
     actually cares about, but Models and The Board both carry a "See pricing"
     CTA of their own (content/about.ts, content/landing.ts), so dropping only
     the third item would leave two paths to the same page. The brand stops
     being a link for the same reason — "/" is the landing page, and its
     pricing teaser is the funnel again.

     Nothing else changes: the bar keeps its shape, and a signed-in reader gets
     AppLinks exactly as before, that bar having never carried the funnel. */
  const inApp = isAppContext();

  return (
    <nav className={styles.nav} aria-label="Main">
      <Container>
        <div className={styles.inner}>
          {inApp ? (
            <span className={styles.brand}>
              <Wordmark compact showTagline={false} />
            </span>
          ) : (
            <Link to="/" className={styles.brand} aria-label="KEV — home">
              <Wordmark compact showTagline={false} />
            </Link>
          )}

          <div className={styles.links}>
            {status === "authenticated" ? (
              <AppLinks />
            ) : inApp ? null : (
              <MarketingLinks status={status} />
            )}
            <AuthControl />
          </div>
        </div>
      </Container>
    </nav>
  );
}
